import React, { useState, useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { eventsAPI, casesAPI, employeesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import { Plus } from 'lucide-react';
import { toast } from 'react-toastify';

const EMPTY = {
  title: '', description: '', start_datetime: '', end_datetime: '',
  location: '', event_type: 'Other', color: '#3b82f6',
};

const EVENT_COLORS = {
  'Court Hearing': '#ef4444',
  'Client Meeting': '#3b82f6',
  'Deadline': '#f59e0b',
  'Consultation': '#8b5cf6',
  'Deposition': '#ec4899',
  'Mediation': '#06b6d4',
  'Internal Meeting': '#10b981',
  'Other': '#6b7280',
};

export default function Calendar() {
  const [events, setEvents] = useState([]);
  const [cases, setCases] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const calRef = useRef(null);

  const load = async () => {
    const { data } = await eventsAPI.list();
    const evts = (data.results || data).map(e => ({
      id: e.id,
      title: e.title,
      start: e.start_datetime,
      end: e.end_datetime,
      color: EVENT_COLORS[e.event_type] || e.color || '#3b82f6',
      extendedProps: e,
    }));
    setEvents(evts);
  };

  useEffect(() => {
    load();
    casesAPI.list().then(r => setCases(r.data.results || r.data));
    employeesAPI.list().then(r => setEmployees(r.data.results || r.data));
  }, []);

  const openNew = (info = null) => {
    setEditing(null);
    const start = info?.dateStr ? `${info.dateStr}T09:00` : '';
    const end = info?.dateStr ? `${info.dateStr}T10:00` : '';
    setForm({ ...EMPTY, start_datetime: start, end_datetime: end });
    setModalOpen(true);
  };

  const openEdit = (info) => {
    const ev = info.event.extendedProps;
    setEditing(ev);
    setForm({
      ...ev,
      start_datetime: ev.start_datetime.slice(0, 16),
      end_datetime: ev.end_datetime.slice(0, 16),
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, color: EVENT_COLORS[form.event_type] || form.color };
      if (editing) { await eventsAPI.update(editing.id, payload); toast.success('Event updated'); }
      else { await eventsAPI.create(payload); toast.success('Event created'); }
      setModalOpen(false);
      load();
    } catch { toast.error('Failed to save event'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!editing || !window.confirm('Delete this event?')) return;
    await eventsAPI.delete(editing.id);
    toast.success('Event deleted');
    setModalOpen(false);
    load();
  };

  const f = (name) => ({ value: form[name] || '', onChange: e => setForm({ ...form, [name]: e.target.value }) });

  return (
    <div className="p-6">
      <PageHeader
        title="Calendar"
        subtitle="Court dates, meetings, deadlines"
        actions={<button className="btn-primary" onClick={() => openNew()}><Plus size={16} />New Event</button>}
      />

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-4">
        {Object.entries(EVENT_COLORS).map(([type, color]) => (
          <div key={type} className="flex items-center gap-1.5 text-xs text-gray-600">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
            {type}
          </div>
        ))}
      </div>

      <div className="card p-0 overflow-hidden">
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay' }}
          events={events}
          dateClick={openNew}
          eventClick={openEdit}
          height={680}
          eventDisplay="block"
          dayMaxEvents={3}
          eventTimeFormat={{ hour: '2-digit', minute: '2-digit', meridiem: false }}
        />
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Event' : 'New Event'}>
        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Title *</label>
            <input className="input" required {...f('title')} />
          </div>
          <div>
            <label className="label">Event Type</label>
            <select className="input" {...f('event_type')}>
              {Object.keys(EVENT_COLORS).map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Linked Case</label>
            <select className="input" {...f('case')}>
              <option value="">None</option>
              {cases.map(c => <option key={c.id} value={c.id}>{c.case_title}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Start *</label>
            <input type="datetime-local" className="input" required {...f('start_datetime')} />
          </div>
          <div>
            <label className="label">End *</label>
            <input type="datetime-local" className="input" required {...f('end_datetime')} />
          </div>
          <div className="col-span-2">
            <label className="label">Location</label>
            <input className="input" {...f('location')} />
          </div>
          <div className="col-span-2">
            <label className="label">Description</label>
            <textarea className="input" rows={2} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="col-span-2 flex justify-between gap-3 pt-2">
            {editing && <button type="button" className="btn-danger" onClick={handleDelete}>Delete</button>}
            <div className="flex gap-3 ml-auto">
              <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Create Event'}</button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
